import { Link } from "react-router"
import { css } from "@emotion/react"
import {
  Date,
  Datetime,
  DayOfWeekAndTimeOfDay,
} from "source/shared/DatetimeFmt"
import { Badge, LegacyHeading } from "source/shared/components"
// @ts-expect-error: no types for @planningcenter/system
import { size } from "@planningcenter/system"
import { MediaThumbnail } from "source/publishing/sermons/shared/MediaThumbnail"

import { EpisodeTimesAPIRecord, HeroAPIRecord } from "./types"
import classNames from "classnames"

const EPISODE_TIME_IS_UPCOMING = "upcoming"
const EPISODE_TIME_IS_ACTIVE = "active"
const EPISODE_TIME_IS_OVER = "over"

type ConditionalLinkProps = {
  children: React.ReactNode
  disabled?: boolean
  to: string
} & React.AnchorHTMLAttributes<HTMLAnchorElement>

function ConditionalLink({
  children,
  disabled,
  to,
  ...props
}: ConditionalLinkProps) {
  if (disabled) {
    return <span {...props}>{children}</span>
  }
  return (
    <Link to={to} {...props}>
      {children}
    </Link>
  )
}

export function ChannelHero({ heroResource }: { heroResource: HeroAPIRecord }) {
  const {
    description,
    is_live: isLive,
    is_published: isPublished,
    library_art_url: libraryArtUrl,
    live_resource_times: liveResourceTimes,
    show_listen_button: showListenButton,
    show_notes_button: showNotesButton,
    show_watch_button: showWatchButton,
    speaker,
    time,
    title,
    upcoming_channel_times: upcomingChannelTimes,
    url,
  } = heroResource.attributes

  const hasDescription = !!description?.trim()
  const showAnyButtons = showWatchButton || showListenButton || showNotesButton

  const liveTimes = liveResourceTimes.filter(
    (episodeTime) =>
      episodeTime.attributes.current_state === EPISODE_TIME_IS_ACTIVE,
  )
  const rest = liveResourceTimes.filter(
    (episodeTime) =>
      episodeTime.attributes.current_state !== EPISODE_TIME_IS_ACTIVE,
  )
  const hasUpcomingTimes = liveResourceTimes.some(
    (episodeTime) =>
      episodeTime.attributes.current_state === EPISODE_TIME_IS_UPCOMING,
  )

  const TimeComponent = isLive ? Datetime : Date

  const watchLabel = isLive ? "Watch live" : "Watch"
  const watchUrl = isLive
    ? `${url}?episode_time_id=${liveTimes[liveTimes.length - 1]?.id}`
    : `${url}?media_intent=video`

  if (!url && upcomingChannelTimes.length > 0) {
    return <ChannelSchedule nextDefaultTimes={upcomingChannelTimes} />
  }

  if (!title) return null

  return (
    <div css={styles.featuredEpisodeContainer}>
      <ConditionalLink
        to={url || ""}
        css={{ img: { borderRadius: 8 } }}
        disabled={!isPublished}
      >
        <MediaThumbnail
          alt={title || ""}
          publishedTime={time || ""}
          thumbnailUrl={libraryArtUrl}
        />
      </ConditionalLink>
      <div className="d-f fd-c g-2">
        <div className="d-f fd-c g-1 ai-fs">
          {isLive && <Badge text="Live" theme="success" />}

          <ConditionalLink to={url || ""} disabled={!isPublished}>
            <LegacyHeading level={3} text={title} />
          </ConditionalLink>

          {!(hasUpcomingTimes && !isLive) && (
            <p className="fs-4 c-tint2 mb-0">
              <TimeComponent start={time} style="long" year={!isLive} />
              {speaker && <span>&nbsp;&bull; {speaker}</span>}
            </p>
          )}
          {description && <p css={styles.featuredDescription}>{description}</p>}
        </div>

        {showAnyButtons && (
          <div className="d-f ai-c g-2">
            {showWatchButton && (
              <Link to={watchUrl} className="btn fs-4">
                {watchLabel}
              </Link>
            )}
            {showNotesButton && (
              <Link
                to={`${url}/notes${isLive ? "?return_to_episode=true" : ""}`}
                className={classNames("btn fs-4", {
                  "secondary-btn": showWatchButton,
                })}
              >
                Take notes
              </Link>
            )}
            {showListenButton && (
              <Link
                to={url + "?media_intent=audio"}
                className={classNames("btn fs-4", {
                  "secondary-btn": showWatchButton || showNotesButton,
                })}
              >
                Listen
              </Link>
            )}
          </div>
        )}
        {hasUpcomingTimes && !showAnyButtons && (
          <EpisodeTimes showBorder={hasDescription} times={rest} />
        )}
      </div>
    </div>
  )
}

export function EpisodeTimes({
  times,
  showBorder,
}: {
  times: EpisodeTimesAPIRecord[]
  showBorder?: boolean
}) {
  return (
    <ul css={[styles.list, showBorder && styles.listBorder]}>
      {times.map((episodeTime) => {
        return (
          <li
            css={{ alignItems: "center" }}
            className="fs-4 c-tint0 d-f ai-fs g-1 ai-c@sm"
            key={episodeTime.id}
          >
            <EpisodeTimeWatchBadge episodeTime={episodeTime} />

            <div className="live-metadata">
              <span className="fs-5">
                <Datetime
                  start={episodeTime.attributes.starts_at}
                  style="long"
                />
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function EpisodeTimeWatchBadge({
  episodeTime,
}: {
  episodeTime: EpisodeTimesAPIRecord
}) {
  const currentState = episodeTime.attributes.current_state as
    | "upcoming"
    | "active"
    | "over"

  const badges = {
    [EPISODE_TIME_IS_OVER]: {
      label: "Over",
      themeClass: undefined,
    },
    [EPISODE_TIME_IS_ACTIVE]: {
      label: "Live",
      themeClass: "success-badge",
    },
    [EPISODE_TIME_IS_UPCOMING]: {
      label: "Upcoming",
      themeClass: undefined,
    },
  }

  return (
    <span
      className={`badge ${badges[currentState].themeClass}`}
      style={{ minWidth: "80px", justifyContent: "center" }}
    >
      {badges[currentState].label}
    </span>
  )
}

function ChannelSchedule({
  nextDefaultTimes,
}: {
  nextDefaultTimes: { starts_at: string }[]
}) {
  return (
    <div css={styles.scheduleBanner}>
      <h2>
        Next Live on <Date start={nextDefaultTimes[0].starts_at} style="long" />
      </h2>
      <ul css={styles.inlineList} className="fs-4">
        <li>Regular Times:</li>

        {nextDefaultTimes.map((nextDefaultTime) => (
          <li key={nextDefaultTime.starts_at} className="c-tint3">
            Every <DayOfWeekAndTimeOfDay start={nextDefaultTime.starts_at} />
          </li>
        ))}
      </ul>
    </div>
  )
}

const styles = {
  featuredEpisodeContainer: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    align-items: start;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
  `,
  featuredDescription: css`
    margin-bottom: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `,
  scheduleGridContainer: css`
    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
  `,
  scheduleBanner: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${size(2)}px;
    background-color: var(--color-tint8);
    border-radius: 4px;
    padding: ${size(3)}px;
  `,
  inlineList: css`
    list-style: none;
    margin: 0 ${size(2)}px 0 0;
    padding: 0;
    display: flex;
    gap: ${size(1)}px;
    flex-wrap: wrap;
  `,
  list: css`
    list-style: none;
    margin: 0;
    padding: 0;
    gap: ${size(1)}px;
    display: flex;
    flex-direction: column;

    .live-metadata {
      display: flex;
      @media (max-width: 719px) {
        position: relative;

        a {
          margin-left: 0;
        }
      }
    }
  `,
  listBorder: css`
    border-top: 1px solid var(--color-tint6);
    padding-top: ${size(2)}px;
  `,
}
